import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import ContentCard from "@/components/base/ContentCard";
import EmptyState from "@/components/base/EmptyState";
import { FilterChipGroup, SortTabs } from "@/components/base/Filters";
import PageIntro from "@/components/base/PageIntro";
import Pagination from "@/components/base/Pagination";
import usePageMeta from "@/hooks/usePageMeta";
import { varietyItems } from "@/mocks/content";
import { formatViews } from "@/lib/format";

const PAGE_SIZE = 6;

const platformOptions = [
  { value: "all", label: "全部平台" },
  { value: "城市卫视", label: "城市卫视" },
  { value: "海岸卫视", label: "海岸卫视" },
  { value: "星海视频", label: "星海视频" },
  { value: "欢乐频道", label: "欢乐频道" },
  { value: "生活卫视", label: "生活卫视" },
];

const timeOptions = [
  { value: "all", label: "全部时间" },
  { value: "week", label: "本周" },
  { value: "month", label: "本月" },
];

const genreOptions = [
  { value: "真人秀", label: "真人秀" },
  { value: "脱口秀", label: "脱口秀" },
  { value: "音乐", label: "音乐" },
  { value: "喜剧", label: "喜剧" },
  { value: "旅行", label: "旅行" },
  { value: "美食", label: "美食" },
  { value: "纪实", label: "纪实" },
];

const sortOptions = [
  { value: "latest", label: "最新发布" },
  { value: "hot", label: "热门优先" },
];

export default function VarietyPage() {
  usePageMeta(
    "综艺动态 - 红桃网",
    "红桃网综艺动态频道，汇总最新综艺节目资讯、播出平台与播出时间，覆盖真人秀、脱口秀、音乐与美食等类型，追综艺不再错过更新。",
    "综艺动态,综艺节目,真人秀,脱口秀,综艺播出时间,红桃网",
  );

  const [platform, setPlatform] = useState("all");
  const [time, setTime] = useState("all");
  const [genres, setGenres] = useState<string[]>([]);
  const [sort, setSort] = useState("latest");
  const [page, setPage] = useState(1);
  const [filtersOpen, setFiltersOpen] = useState(false);

  const filtered = useMemo(() => {
    const list = varietyItems.filter((item) => {
      const platformOk = platform === "all" || item.platform === platform;
      const timeOk =
        time === "all" ||
        (time === "week" && item.timeRange === "week") ||
        (time === "month" && item.timeRange !== "older");
      const genreOk = genres.length === 0 || genres.some((genre) => item.genres.includes(genre));
      return platformOk && timeOk && genreOk;
    });

    return list.sort((a, b) => {
      if (sort === "hot") return b.views - a.views;
      return new Date(b.publishedAt).getTime() - new Date(a.publishedAt).getTime();
    });
  }, [platform, time, genres, sort]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageItems = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const toggleGenre = (value: string) => {
    setGenres((prev) => (prev.includes(value) ? prev.filter((item) => item !== value) : [...prev, value]));
    setPage(1);
  };

  const resetFilters = () => {
    setPlatform("all");
    setTime("all");
    setGenres([]);
    setSort("latest");
    setPage(1);
  };

  const activeFilterCount = (platform !== "all" ? 1 : 0) + (time !== "all" ? 1 : 0) + genres.length;
  const hotVariety = [...varietyItems].sort((a, b) => b.views - a.views).slice(0, 5);
  const weekSchedule = [...varietyItems]
    .filter((item) => item.timeRange === "week")
    .sort((a, b) => b.views - a.views)
    .slice(0, 4);

  return (
    <>
      <PageIntro
        kicker="综艺频道"
        title="综艺动态"
        description="最新综艺节目资讯与播出信息。哪档节目值得追、什么时候播、在哪看，都在这一页说清楚。"
        breadcrumbs={[{ label: "首页", to: "/" }, { label: "综艺动态" }]}
        extra={
          <div className="flex items-center gap-5 text-sm">
            <div>
              <p className="font-label text-xl font-extrabold text-foreground-950">{varietyItems.length}</p>
              <p className="text-xs text-foreground-500">档节目在播</p>
            </div>
            <span className="h-8 w-px bg-background-300" />
            <div>
              <p className="font-label text-xl font-extrabold text-primary-600">5</p>
              <p className="text-xs text-foreground-500">个播出平台</p>
            </div>
          </div>
        }
      />

      <div className="mx-auto w-full max-w-portal px-4 py-6 md:px-6 md:py-8">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-[minmax(0,1fr)_300px] lg:gap-9">
          <div>
            <div className="rounded-lg border border-background-200 bg-background-50">
              <div className="flex items-center justify-between gap-3 border-b border-background-200 px-4 py-3">
                <div className="flex items-center gap-2">
                  <i className="ri-filter-3-line text-primary-500" />
                  <span className="font-heading text-sm font-semibold text-foreground-900">筛选条件</span>
                  {activeFilterCount > 0 && (
                    <span className="rounded-full bg-primary-500 px-1.5 py-0.5 font-label text-[10px] text-background-50">
                      {activeFilterCount}
                    </span>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => setFiltersOpen((prev) => !prev)}
                  className="flex cursor-pointer items-center gap-1 text-xs text-foreground-500 md:hidden"
                >
                  {filtersOpen ? "收起" : "展开"}
                  <i className={filtersOpen ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"} />
                </button>
              </div>

              <div className={`${filtersOpen ? "block" : "hidden"} space-y-3 px-4 py-4 md:block`}>
                <FilterChipGroup
                  label="播出平台"
                  options={platformOptions}
                  selected={[platform]}
                  onSelect={(value) => {
                    setPlatform(value);
                    setPage(1);
                  }}
                />
                <FilterChipGroup
                  label="播出时间"
                  options={timeOptions}
                  selected={[time]}
                  onSelect={(value) => {
                    setTime(value);
                    setPage(1);
                  }}
                />
                <FilterChipGroup label="综艺类型" options={genreOptions} selected={genres} onSelect={toggleGenre} />
              </div>

              <div className="flex flex-wrap items-center justify-between gap-3 border-t border-background-200 px-4 py-3">
                <p className="text-xs text-foreground-500">
                  共 <span className="font-label font-semibold text-foreground-900">{filtered.length}</span> 档节目
                  {filtered.length > 0 && (
                    <span className="ml-1 hidden sm:inline">· 第 {currentPage} / {totalPages} 页</span>
                  )}
                </p>
                <SortTabs
                  options={sortOptions}
                  value={sort}
                  onChange={(value) => {
                    setSort(value);
                    setPage(1);
                  }}
                />
              </div>
            </div>

            {pageItems.length > 0 ? (
              <>
                <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
                  {pageItems.map((item) => (
                    <ContentCard key={item.id} item={item} showSummaryAlways />
                  ))}
                </div>
                <div className="mt-6">
                  <Pagination page={currentPage} totalPages={totalPages} onChange={setPage} />
                </div>
              </>
            ) : (
              <div className="mt-4">
                <EmptyState
                  title="没有符合条件的综艺内容"
                  description="换个播出平台或时间范围试试，也可以清除综艺类型的筛选条件。"
                  actionLabel="重置筛选"
                  onAction={resetFilters}
                  icon="ri-mic-line"
                />
              </div>
            )}
          </div>

          <aside className="space-y-4 self-start">
            <div className="rounded-lg border border-background-200 bg-background-50 p-4">
              <h2 className="flex items-center gap-2 font-heading text-sm font-bold text-foreground-950">
                <i className="ri-calendar-check-line text-primary-500" />
                本周播出
              </h2>
              <ul className="mt-3 divide-y divide-background-100">
                {weekSchedule.map((item) => (
                  <li key={item.id} className="py-2.5">
                    <Link
                      to={`/variety/${item.id}`}
                      className="group flex items-center justify-between gap-3 text-[13px]"
                    >
                      <span className="truncate text-foreground-800 transition-colors group-hover:text-primary-600">
                        {item.title}
                      </span>
                      <span className="shrink-0 rounded-sm bg-background-100 px-1.5 py-0.5 font-label text-[10px] text-foreground-500">
                        {item.airTime.replace("每周", "周")}
                      </span>
                    </Link>
                    <p className="mt-1 text-[11px] text-foreground-400">{item.platform}</p>
                  </li>
                ))}
              </ul>
            </div>

            <div className="rounded-lg border border-background-200 bg-background-50">
              <div className="flex items-center justify-between border-b border-background-200 px-4 py-3">
                <h2 className="flex items-center gap-2 font-heading text-sm font-bold text-foreground-950">
                  <i className="ri-fire-fill text-primary-500" />
                  综艺热榜
                </h2>
                <span className="font-label text-[10px] tracking-widest text-foreground-400">热榜</span>
              </div>
              <ol className="divide-y divide-background-100 px-4">
                {hotVariety.map((item, index) => (
                  <li key={item.id}>
                    <Link to={`/variety/${item.id}`} className="group flex items-center gap-3 py-3">
                      <span
                        className={[
                          "flex h-5 w-5 shrink-0 items-center justify-center rounded-sm font-label text-[11px] font-bold",
                          index < 3 ? "bg-primary-500 text-background-50" : "bg-background-100 text-foreground-500",
                        ].join(" ")}
                      >
                        {index + 1}
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block text-[13px] text-foreground-800 transition-colors group-hover:text-primary-600 line-clamp-1">
                          {item.title}
                        </span>
                        <span className="mt-0.5 block text-[11px] text-foreground-400">
                          {formatViews(item.views)} 次浏览
                        </span>
                      </span>
                    </Link>
                  </li>
                ))}
              </ol>
            </div>

            <div className="rounded-lg bg-accent-100 p-4">
              <p className="flex items-center gap-1.5 text-sm font-semibold text-accent-950">
                <i className="ri-megaphone-line" />
                追综艺小提示
              </p>
              <p className="mt-1.5 text-xs leading-relaxed text-accent-900/80">
                各平台排播时间可能临时调整，具体以官方播出信息为准。本页时间仅为演示数据。
              </p>
            </div>
          </aside>
        </div>
      </div>
    </>
  );
}